import {Component, OnInit} from '@angular/core';
import {CoreService, PlanningService, ProleisObject, Task, UserService,} from 'proleis-rest-client';
import {ActivatedRoute} from '@angular/router';
import {combineLatest, debounceTime, exhaustMap, map, Observable, shareReplay, startWith, tap,} from 'rxjs';
import {BreakpointService, CurrentViewService, LoadingService, NotificationService,} from 'proleis-web-app';
import {FormControl} from '@angular/forms';
import {PpsService} from "../board/pps.service";
import {GUID_TYPE_PART, GUID_TYPE_PSE} from "../../../../../p4m/guids";

const SETTING_ASSIGNED_TASKS = 'p4m_selected_assigned_tasks';
const SETTINGS_EXCLUDE_FINISHED = 'p4m_selected_exclude_finished';
const SETTINGS_EXCLUDE_UNPLANNED = 'p4m_selected_exclude_unplanned';

@Component({
  selector: 'p4m-view-tasks',
  templateUrl: 'view-tasks.component.html',
  styleUrls: ['view-tasks.component.scss'],
})
export class ViewTasksComponent implements OnInit {
  expanded = false;
  user$: Observable<ProleisObject>;
  parent$: Observable<ProleisObject>;
  onlyAssignedTasks: FormControl = new FormControl<boolean>(localStorage.getItem(SETTING_ASSIGNED_TASKS) === 'true');
  includeFinished: FormControl = new FormControl<boolean>(localStorage.getItem(SETTINGS_EXCLUDE_FINISHED) === 'true');
  includeUnplanned: FormControl = new FormControl<boolean>(localStorage.getItem(SETTINGS_EXCLUDE_UNPLANNED) === 'true');
  searchControl: FormControl = new FormControl<string>('');
  part$: Observable<ProleisObject>;
  tasks$: Observable<Task[]>;
  isPhone$: Observable<boolean>;

  currentBooking$: Observable<ProleisObject[]>;

  constructor(
    public currentViewService: CurrentViewService,
    private coreService: CoreService,
    private activatedRoute: ActivatedRoute,
    private userService: UserService,
    private breakpointService: BreakpointService,
    private notificationService: NotificationService,
    private planningService: PlanningService,
    public loadingService: LoadingService,
    public ppsService: PpsService
  ) {
  }

  ngOnInit() {
    this.user$ = this.userService.getCurrentUser();
    this.getCurrentBooking();

    this.activatedRoute.params.subscribe((params) => {
      this.part$ = this.coreService.getObjectById(
        params.partId,
        this.getPartAliasFields(),
      );
      this.tasks$ = this.getTasks();
      this.parent$ = this.getParentObject();
    })

    this.expanded = localStorage.getItem('p4m_view_tasks_panel_expanded') === 'true';
    this.isPhone$ = this.breakpointService.isSmall();
    this.subscribeToFormControlChanges();
  }

  private subscribeToFormControlChanges() {
    this.onlyAssignedTasks.valueChanges
      .pipe(
        shareReplay(1),
        debounceTime(250),
        tap((value) => {
          this.tasks$ = this.getTasks();
          localStorage.setItem(SETTING_ASSIGNED_TASKS, value.toString());
        }),
      )
      .subscribe();

    this.includeFinished.valueChanges
      .pipe(
        shareReplay(1),
        debounceTime(250),
        tap((value) => {
          this.tasks$ = this.getTasks();
          localStorage.setItem(SETTINGS_EXCLUDE_FINISHED, value.toString());
        })
      )
      .subscribe();

    this.includeUnplanned.valueChanges
      .pipe(
        shareReplay(1),
        debounceTime(250),
        tap((value) => {
          this.tasks$ = this.getTasks();
          localStorage.setItem(SETTINGS_EXCLUDE_UNPLANNED, value.toString());
        })
      ).subscribe();
  }

  getTaskAliasFields() {
    const aliasFields = ['INFOTEXT=OBJECT_ID.INFOTEXT', this.ppsService.getDynamicAliasFields(), this.ppsService.getHideIfAlias()];
    return aliasFields.join(';');
  }

  getPartAliasFields() {
    return 'PARENTNAME=PARENT.TEXT;TYPGUID=TYP_ID.GUID;' +
      'PARENT=$NUM($PEEK($FILTER(PARENTSNOTHIS(PATH=VIEW),$OR(TYP_ID.GUID="' + GUID_TYPE_PSE + '", TYP_ID.GUID="' + GUID_TYPE_PART + '"))).OBJECT_ID)';
  }

  startBooking(task: Task) {
    this.userService.startTimeTracking(task.OBJECT_ID).subscribe((result) => {
      this.notificationService.showNotification('Buchung gestartet');
      this.getTasks();
      this.getCurrentBooking();
    });
  }

  getTasks(): Observable<Task[]> {
    const filter$ = this.searchControl.valueChanges.pipe(
      startWith(''),
      debounceTime(250),
    );
    return this.part$
      .pipe(
        exhaustMap((part) => {
          return combineLatest([
            filter$,
            this.planningService.getTasks(
              part.OBJECT_ID,
              this.onlyAssignedTasks.value,
              !this.includeFinished.value,
              !this.includeUnplanned.value,
              this.getTaskAliasFields(),
            ),
          ]).pipe(
            map(([filter, tasks]) => {
              return this.filterTasks(tasks, filter);
            }),
          );
        }),
      )
      .pipe(
        map((tasks) => {
          return this.sortTasks(tasks);
        }),
      );
  }

  filterTasks(tasks: Task[], filter: string): Task[] {
    return tasks.filter((t) =>
      t.TEXT.toLowerCase().includes(filter.toLowerCase()),
    );
  }

  sortTasks(tasks: Task[]): Task[] {
    return tasks.sort((a, b) => {
      if (a.STARTPLAN > b.STARTPLAN) {
        return -1;
      }
      if (a.STARTPLAN < b.STARTPLAN) {
        return 1;
      }
      return 0;
    });
  }

  getCurrentBooking() {
    const aliasFields =
      'AFONAME=AFO_ID.TEXT;TOOLNAME=BEMI_ID.TEXT;RESNAME=RES_ID.TEXT';
    this.currentBooking$ = this.userService.getCurrentBookings(aliasFields);
  }

  onTaskUpdated() {
    this.tasks$ = this.getTasks();
  }

  openDrawer() {
    this.currentViewService.drawer.toggle();
  }

  getParentObject() {
    return this.part$.pipe(
      exhaustMap(part => {
        if (part.PARENT) {
          return this.coreService.getObjectById<ProleisObject>(part.PARENT);
        }
      })
    )
  }

  onExpanded(event: boolean) {
    localStorage.setItem('p4m_view_tasks_panel_expanded', event.toString());
  }

  protected readonly Number = Number;
}
